import { useState, useEffect } from 'react';
import { Card } from '../App';
import { FileText } from 'lucide-react';
import { applyClassificationToCard, CARD_CLASSIFICATION_OVERRIDES } from '../lib/card-classification';

interface DecklistImportProps {
  onImport: (cards: Card[]) => void;
  onImportSideboard?: (cards: Card[]) => void;
  onFormatDetect?: (format: string) => void;
  onDeckSizeDetect?: (size: number) => void;
  currentDeck?: Card[];
}

// Helper function to convert deck to text format
function deckToText(deck: Card[]): string {
  if (!deck || deck.length === 0) return '';

  return deck
    .map(card => {
      const name = card.name;
      // If not a DFC name, return as-is
      if (!name.includes(' // ')) return `${card.quantity} ${name}`;

      // Room cards (Duskmourn) need the full "Front // Back" name for Arena
      const isRoom = card.type?.toLowerCase().includes('room');
      if (isRoom) return `${card.quantity} ${name}`;

      // Other DFCs (Transform, MDFC, Marvel) - use front face only
      return `${card.quantity} ${name.split(' // ')[0]}`;
    })
    .join('\n');
}

export function DecklistImport({ onImport, onImportSideboard, onFormatDetect, onDeckSizeDetect, currentDeck = [] }: DecklistImportProps) {
  const [decklistText, setDecklistText] = useState('');
  const [isImporting, setIsImporting] = useState(false);

  // Initialize textarea with current deck on mount and when deck changes
  useEffect(() => {
    if (currentDeck.length > 0 && !decklistText) {
      setDecklistText(deckToText(currentDeck));
    }
  }, [currentDeck, decklistText]);

  const parseDecklistLine = async (line: string): Promise<Card | null> => {
    // Match patterns like:
    // "4 Lightning Bolt"
    // "4x Lightning Bolt"
    // "4 Lightning Bolt (2XM) 123"
    const match = line.match(/^(\d+)x?\s+([^(]+?)(?:\s*\([^)]+\)\s*\d*)?$/i);
    if (!match) return null;

    const quantity = parseInt(match[1]);
    // Strip DFC back face if present ("Front // Back" -> "Front")
    let cardName = match[2].trim();
    if (cardName.includes(' // ')) {
      cardName = cardName.split(' // ')[0];
    }

    try {
      // Add timeout to prevent stuck imports
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

      let response = await fetch(
        `https://api.scryfall.com/cards/named?fuzzy=${encodeURIComponent(cardName)}`,
        { signal: controller.signal }
      );
      clearTimeout(timeoutId);

      if (response.ok) {
        let data = await response.json();

        // Check if this is a Universes Beyond card that has an Arena replacement
        // UB cards often have set codes like 'spg', 'pip', 'who', 'ltr', 'sld' with special frames
        // and may have related cards via all_parts (spellbook relationship)
        const ubSets = ['spg', 'pip', 'who', 'ltr', 'sld', 'ltc', 'acr', 'rex', 'bot'];
        const isUniversesBeyond = ubSets.includes(data.set) ||
          data.frame_effects?.includes('showcase') ||
          data.security_stamp === 'triangle';

        // If it's a UB card, try to find the Arena-legal replacement via all_parts
        if (isUniversesBeyond && data.all_parts) {
          const replacement = data.all_parts.find((part: { component: string; name: string; uri: string }) =>
            part.component === 'combo_piece' || part.component === 'token'
          );
          // Or find a card with the same oracle_id but different name (functional reprint)
          if (!replacement) {
            // Search for cards with same oracle_id that are Arena-legal
            const oracleId = data.oracle_id;
            if (oracleId) {
              try {
                const oracleController = new AbortController();
                const oracleTimeoutId = setTimeout(() => oracleController.abort(), 5000);
                const printsResponse = await fetch(
                  `https://api.scryfall.com/cards/search?q=oracle_id:${oracleId}+game:arena&unique=cards`,
                  { signal: oracleController.signal }
                );
                clearTimeout(oracleTimeoutId);
                if (printsResponse.ok) {
                  const printsData = await printsResponse.json();
                  // Find a version that's not from a UB set
                  const arenaVersion = printsData.data?.find((card: { set: string }) =>
                    !ubSets.includes(card.set)
                  );
                  if (arenaVersion) {
                    console.log(`Found Arena replacement for ${data.name}: ${arenaVersion.name}`);
                    data = arenaVersion;
                  }
                }
              } catch (e) {
                // Fallback to original card if oracle search fails
                console.warn('Could not find Arena replacement, using original:', e);
              }
            }
          }
        }

        // Map Scryfall rarity to our abbreviations
        const rarityMap: Record<string, 'C' | 'U' | 'R' | 'M' | 'B'> = {
          common: 'C',
          uncommon: 'U',
          rare: 'R',
          mythic: 'M',
          special: 'R',
          bonus: 'R',
        };

        // Detect if land enters tapped
        const oracleText = data.oracle_text?.toLowerCase() || data.card_faces?.[0]?.oracle_text?.toLowerCase() || '';

        // For DFCs, use only the front face type line; for single-faced cards, use the main type line
        // IMPORTANT: For transform cards, data.type_line is "Front // Back" so we MUST use card_faces[0]
        const frontFaceTypeLine = data.card_faces?.[0]?.type_line ?? data.type_line ?? '';
        const fullTypeLine = data.type_line || '';

        // If the card has card_faces, it's a DFC - use the front face type explicitly
        // If data.type_line contains "//" it's a DFC format like "Artifact // Land" - only check the left side
        let typeToCheck = frontFaceTypeLine;
        if (fullTypeLine.includes('//')) {
          typeToCheck = fullTypeLine.split('//')[0].trim();
        }

        // isLand should only check the FRONT face - transform cards with land backs are NOT lands
        const isLand = typeToCheck.toLowerCase().includes('land');
        const entersTapped = isLand &&
          (oracleText.includes('enters the battlefield tapped') ||
            oracleText.includes('enters tapped'));
        const isBasicLand = typeToCheck.toLowerCase().includes('basic land');

        // Determine if tapped is conditional (optional) or always
        // Conditional: has "unless" or "pay life" or "if you control" that can prevent tapped entry
        let tapCondition: 'always' | 'conditional' | undefined;
        if (entersTapped) {
          const hasUnless = oracleText.includes('unless');
          const hasPay = oracleText.includes('pay') && oracleText.includes('life');
          const hasIf = oracleText.includes('if you control');
          const hasReveal = oracleText.includes('reveal') && oracleText.includes('from your hand');

          if (hasUnless || hasPay || hasIf || hasReveal) {
            tapCondition = 'conditional';
          } else {
            tapCondition = 'always';
          }
        }

        // Detect Partner, Partner with, and Background keywords
        const keywords = data.keywords || [];
        const hasPartner = keywords.includes('Partner') && !oracleText.includes('partner with');

        // Parse "Partner with [name]" - look for the pattern in oracle text
        let hasPartnerWith: string | undefined;
        const partnerWithMatch = oracleText.match(/partner with ([^(]+)/i);
        if (partnerWithMatch) {
          hasPartnerWith = partnerWithMatch[1].trim();
        }

        // Background detection - check type line for "Background" subtype
        const isBackground = fullTypeLine.toLowerCase().includes('background');
        const choosesBackground = keywords.includes('Choose a Background') ||
          oracleText.includes('choose a background');

        // Companion detection
        const isCompanionType = keywords.includes('Companion');
        let companionCondition: string | undefined;
        if (isCompanionType) {
          // Extract companion restriction text
          const companionMatch = oracleText.match(/companion\s*—\s*([^(]+)/i);
          if (companionMatch) {
            companionCondition = companionMatch[1].trim();
          }
        }

        // Extra card detection (planes, schemes, etc.)
        const typeLineLower = fullTypeLine.toLowerCase();
        let isExtraCard = false;
        let extraCardType: 'plane' | 'phenomenon' | 'scheme' | 'vanguard' | 'conspiracy' | 'other' | undefined;

        if (typeLineLower.includes('plane ') || typeLineLower.includes('plane —')) {
          isExtraCard = true;
          extraCardType = 'plane';
        } else if (typeLineLower.includes('phenomenon')) {
          isExtraCard = true;
          extraCardType = 'phenomenon';
        } else if (typeLineLower.includes('scheme')) {
          isExtraCard = true;
          extraCardType = 'scheme';
        } else if (typeLineLower.includes('vanguard')) {
          isExtraCard = true;
          extraCardType = 'vanguard';
        } else if (typeLineLower.includes('conspiracy')) {
          isExtraCard = true;
          extraCardType = 'conspiracy';
        }

        // Detect double-faced cards (MDFC, Transform, etc.)
        // Transform cards have layout='transform', MDFCs have layout='modal_dfc'
        // Some transform cards have image_uris in card_faces, others don't
        const hasTwoFaces = !!(data.card_faces && data.card_faces.length > 1);
        const isDoubleFaced = hasTwoFaces && (
          data.layout === 'transform' ||
          data.layout === 'modal_dfc' ||
          data.layout === 'double_faced_token' ||
          data.layout === 'flip' ||
          data.layout === 'reversible_card' ||
          !!(data.card_faces?.[1]?.image_uris)
        );
        // For back image: try card_faces first, then construct from Scryfall's image pattern
        const backImageUrl = isDoubleFaced
          ? (data.card_faces?.[1]?.image_uris?.normal ||
            (data.card_faces?.[1] ? `https://cards.scryfall.io/normal/back/${data.id.slice(0, 1)}/${data.id.slice(1, 2)}/${data.id}.jpg` : undefined))
          : undefined;
        const backName = isDoubleFaced ? data.card_faces?.[1]?.name : undefined;
        // For MDFCs: store back face type and CMC for per-plan face selection
        const backType = isDoubleFaced ? data.card_faces?.[1]?.type_line : undefined;
        const backCmc = isDoubleFaced ? (data.card_faces?.[1]?.cmc ?? 0) : undefined;

        // Auto-detect Ramp (mana dorks and mana rocks)
        // Patterns: "{T}: Add {G}", "add one mana", "add mana of any color", etc.
        let rampType: string | undefined;
        let rampNet: number | undefined;
        let rampTurn: number | undefined;

        // Broader mana production detection
        const addsMana = !isLand && (
          oracleText.includes('{t}: add') ||
          oracleText.includes('{t}, sacrifice') && oracleText.includes('add') ||
          oracleText.match(/\{t\}.*add \{[wubrgc]\}/) ||
          oracleText.includes('add one mana') ||
          oracleText.includes('add mana') ||
          oracleText.includes('add that much mana') ||
          oracleText.match(/add \w+ mana/)
        );

        if (addsMana) {
          // Count mana symbols produced (basic detection)
          const manaMatch = oracleText.match(/add \{([wubrgc])\}/g);
          if (manaMatch) {
            rampNet = manaMatch.length;
          } else if (oracleText.includes('add two mana') || oracleText.includes('add {c}{c}')) {
            rampNet = 2;
          } else if (oracleText.includes('add three mana')) {
            rampNet = 3;
          } else {
            // Default to 1 mana for "add one mana" or variable amounts
            rampNet = 1;
          }

          // Determine ramp type based on card type
          if (typeToCheck.toLowerCase().includes('creature')) {
            rampType = 'Creature';
            rampTurn = (data.cmc || 1) + 1; // Creatures tap next turn (summoning sickness)
          } else if (typeToCheck.toLowerCase().includes('artifact')) {
            rampType = 'Artifact';
            rampTurn = (data.cmc || 1) + 1; // Most artifacts also have summoning sickness unless they say otherwise
          } else if (typeToCheck.toLowerCase().includes('enchantment')) {
            rampType = 'Spell';
            rampTurn = data.cmc || 1;
          } else {
            rampType = 'Spell';
            rampTurn = data.cmc || 1;
          }
        }

        // Auto-detect Cantrip (cards that draw)
        // Patterns: "draw a card", "draw X cards", "draw two cards"
        let cantrip: number | undefined;

        if (oracleText.includes('draw a card')) {
          cantrip = 1;
        } else if (oracleText.includes('draw two cards')) {
          cantrip = 2;
        } else if (oracleText.includes('draw three cards')) {
          cantrip = 3;
        } else {
          const drawMatch = oracleText.match(/draw (\d+) cards?/);
          if (drawMatch) {
            cantrip = parseInt(drawMatch[1]);
          }
        }

        // Surveil detection
        let surveilAmount: number | undefined;
        const surveilMatch = oracleText.match(/surveil (\d+)/);
        if (surveilMatch) {
          surveilAmount = parseInt(surveilMatch[1]);
        }

        // Scry detection  
        let scryAmount: number | undefined;
        const scryMatch = oracleText.match(/scry (\d+)/);
        if (scryMatch) {
          scryAmount = parseInt(scryMatch[1]);
        }

        // Build base card object
        const baseCard: Card = {
          id: `${data.id}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          name: data.name,
          quantity: quantity,
          type: fullTypeLine,
          // For adventure cards, data.mana_cost is combined "{6}{G} // {2}{G}" which breaks display.
          // Use card_faces[0].mana_cost (creature cost only) for adventure/split layouts.
          manaCost: (data.layout === 'adventure' || data.layout === 'split')
            ? (data.card_faces?.[0]?.mana_cost || data.mana_cost || '')
            : (data.mana_cost || data.card_faces?.[0]?.mana_cost || ''),
          cmc: data.cmc,
          colors: data.colors || data.card_faces?.[0]?.colors || [],
          colorIdentity: data.color_identity || [],
          imageUrl: data.image_uris?.normal || data.card_faces?.[0]?.image_uris?.normal || '',
          scryfallId: data.id,
          isLand: isLand,
          rarity: isBasicLand ? 'B' : rarityMap[data.rarity] || 'C',
          entersTapped: entersTapped,
          tapCondition: tapCondition,
          isDoubleFaced: isDoubleFaced,
          layout: data.layout,  // 'modal_dfc', 'transform', 'flip', etc.
          backImageUrl: backImageUrl,
          backName: backName,
          backType: backType,
          backCmc: backCmc,
          hasPartner: hasPartner,
          hasPartnerWith: hasPartnerWith,
          isBackground: isBackground,
          choosesBackground: choosesBackground,
          isCompanion: isCompanionType,
          companionCondition: companionCondition,
          isExtraCard: isExtraCard,
          extraCardType: extraCardType,
          // Legacy auto-detected ramp & card draw properties
          rampType: rampType,
          rampNet: rampNet,
          rampTurn: rampTurn,
          cantrip: cantrip,
          surveilAmount: surveilAmount,
          scryAmount: scryAmount,
          // Pricing data from Scryfall
          priceUsd: data.prices?.usd ? parseFloat(data.prices.usd) : null,
          priceUsdFoil: data.prices?.usd_foil ? parseFloat(data.prices.usd_foil) : null,
          priceEur: data.prices?.eur ? parseFloat(data.prices.eur) : null,
          setName: data.set_name,
          setCode: data.set,
          purchaseUrlTcg: data.purchase_uris?.tcgplayer,
          purchaseUrlCardmarket: data.purchase_uris?.cardmarket,
          scryfallUrl: data.scryfall_uri,
        };

        // Apply R1-R6 and C1-C5 classification
        // First check for known overrides, then fall back to auto-detection
        const override = CARD_CLASSIFICATION_OVERRIDES[data.name];
        if (override) {
          // Apply known overrides
          Object.assign(baseCard, override);
        } else {
          // Auto-detect from oracle text
          const classified = applyClassificationToCard(
            baseCard,
            oracleText,
            fullTypeLine,
            data.keywords || []
          );
          Object.assign(baseCard, classified);
        }

        return baseCard;
      }
    } catch (error) {
      console.error(`Error importing ${cardName}:`, error);
    }

    return null;
  };

  const handleImport = async () => {
    if (!decklistText.trim()) return;

    setIsImporting(true);
    const lines = decklistText.split('\n');
    const mainDeckCards: Card[] = [];
    const sideboardCards: Card[] = [];
    const commanderCards: Card[] = [];

    // Track which section we're in
    let currentSection: 'main' | 'sideboard' | 'commander' | 'extra' | 'skip' = 'main';

    try {
      let index = 0;
      for (const rawLine of lines) {
        const line = rawLine.trim();

        // Skip empty lines
        if (!line) continue;

        // Check for section headers (case insensitive)
        const lowerLine = line.toLowerCase();

        // Commander section headers
        if (lowerLine === 'commander' || lowerLine.startsWith('commander:') ||
          lowerLine === 'partner commanders' || lowerLine.startsWith('partner commanders:') ||
          lowerLine === 'background' || lowerLine.startsWith('background:')) {
          currentSection = 'commander';
          continue;
        }

        // Main deck section headers
        if (lowerLine === 'deck' || lowerLine.startsWith('deck:') ||
          lowerLine === 'main deck' || lowerLine.startsWith('main deck:') ||
          lowerLine === 'maindeck' || lowerLine.startsWith('maindeck:')) {
          currentSection = 'main';
          continue;
        }

        // Sideboard section headers (includes companions) - use includes for flexible matching
        if (lowerLine === 'sideboard' || lowerLine.startsWith('sideboard:') ||
          lowerLine.includes('sideboard/') || lowerLine.includes('/companions') ||
          lowerLine === 'companions' || lowerLine.startsWith('companions:')) {
          currentSection = 'sideboard';
          continue;
        }

        // Extra cards section headers (planes, schemes, etc.)
        if (lowerLine === 'extra' || lowerLine.startsWith('extra:') ||
          lowerLine === 'extra cards' || lowerLine.startsWith('extra cards:') ||
          lowerLine === 'other' || lowerLine.startsWith('other:')) {
          currentSection = 'extra';
          continue;
        }

        // Skip metadata sections like "About", "Name", etc.
        if (lowerLine === 'about' || lowerLine.startsWith('about:') ||
          lowerLine.startsWith('name ') || lowerLine.startsWith('name:')) {
          currentSection = 'skip';
          continue;
        }

        // Skip lines that look like section labels (no numbers at start)
        if (!/^\d/.test(line)) {
          continue;
        }

        const card = await parseDecklistLine(line);
        if (card && card.scryfallId) {
          // Ensure unique ID by adding index
          card.id = `${card.scryfallId}-${Date.now()}-${index}-${Math.random().toString(36).substr(2, 9)}`;

          if (currentSection === 'commander') {
            // Commanders go to sideboard zone with special flag
            // They count in Side count, not Main, and don't affect curve
            card.isCommander = true;
            card.isSideboard = true; // Commanders are in command zone, not mainboard
            sideboardCards.push(card);
          } else if (currentSection === 'main') {
            mainDeckCards.push(card);
          } else if (currentSection === 'sideboard') {
            // Sideboard cards: display category + Plan A quantity = 0
            card.isSideboard = true;
            card.planQuantities = { A: 0 }; // Not in main deck for Plan A
            sideboardCards.push(card);
          } else if (currentSection === 'extra') {
            // Extra cards (planes, schemes, etc.) - marked as extra
            card.isExtraCard = true;
            card.isSideboard = true; // Store with sideboard for import
            sideboardCards.push(card);
          }
          index++;
        }
        // Small delay to avoid rate limiting
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      // Import main deck
      if (mainDeckCards.length > 0) {
        onImport(mainDeckCards);
      }

      // Import sideboard if we have a callback for it
      if (sideboardCards.length > 0 && onImportSideboard) {
        onImportSideboard(sideboardCards);
      }

      // Auto-detect format based on total card count
      if (onFormatDetect) {
        const totalMainCards = mainDeckCards.reduce((sum, c) => sum + c.quantity, 0);
        const totalSideCards = sideboardCards.reduce((sum, c) => sum + c.quantity, 0);
        const hasCommander = sideboardCards.some(c => c.isCommander);

        // EDH: 99 main + 1 commander = 100, or has Commander section
        if (hasCommander || totalMainCards >= 99 || (totalMainCards + totalSideCards) >= 100) {
          onFormatDetect('EDH');
        } else if (totalMainCards >= 75 && totalMainCards <= 80) {
          onFormatDetect('Constructed 80');
        } else if (totalMainCards >= 55 && totalMainCards <= 65) {
          onFormatDetect('Constructed 60');
        } else if (totalMainCards <= 45) {
          onFormatDetect('Limited');
        }
        // If no clear match, don't change format
      }

      // Auto-snap deck size preset using midpoint logic
      if (onDeckSizeDetect) {
        const totalMainCards = mainDeckCards.reduce((sum, c) => sum + c.quantity, 0);
        if (totalMainCards <= 49) {
          onDeckSizeDetect(40);
        } else if (totalMainCards >= 82) {
          onDeckSizeDetect(99);
        } else if (totalMainCards >= 80) {
          onDeckSizeDetect(80);
        } else {
          onDeckSizeDetect(60);
        }
      }
    } catch (error) {
      console.error('Error importing decklist:', error);
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="relative">
        <textarea
          value={decklistText}
          onChange={(e) => setDecklistText(e.target.value)}
          placeholder="4 Lightning Bolt&#10;4 Monastery Swiftspear&#10;20 Mountain"
          rows={8}
          className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder:text-white/40 focus:outline-none focus:border-purple-500/50 font-mono resize-none"
          disabled={isImporting}
        />
        {!decklistText && (
          <FileText className="absolute right-3 top-3 size-5 text-white/20" />
        )}
      </div>

      <div className="flex items-start justify-between gap-3">
        <p className="text-xs text-white/50 flex-1">
          Formats: <span className="text-white/70">4 Lightning Bolt</span> or{' '}
          <span className="text-white/70">4x Lightning Bolt (2XM) 123</span>
        </p>

        <button
          onClick={handleImport}
          disabled={isImporting || !decklistText.trim()}
          className="px-4 py-2 bg-purple-500 hover:bg-purple-600 disabled:bg-white/10 disabled:text-white/40 text-white rounded-lg text-sm transition-colors whitespace-nowrap"
        >
          {isImporting ? 'Importing...' : 'Import List'}
        </button>
      </div>
    </div>
  );
}